import type { Identifier } from "ra-core";

import type { GulaOrder, GulaPayment, GulaSubscription } from "../gulaTypes";

/**
 * Profit of one sale. Revenue is what the client pays (VAT included); every
 * cost is entered without VAT, because the company recovers the VAT it pays.
 * Pure functions: the pages only gather the data and render the result.
 */

export type CostSettings = {
  id: Identifier;
  vat_rate: number;
  equipment_cost: number;
  three_way_faucet_cost: number;
  filters_year_cost: number;
  membrane_cost: number;
  install_aux_cost: number;
  install_three_way_cost: number;
  shipping_aux_cost: number;
  shipping_three_way_cost: number;
  filters_shipping_year_cost: number;
  membrane_shipping_cost: number;
  fee_card_pct: number;
  fee_card_fixed: number;
  fee_klarna_pct: number;
  fee_klarna_fixed: number;
  fee_financing_pct: number;
  fee_financing_fixed: number;
  fee_recurring_pct: number;
  fee_recurring_fixed: number;
  /** Stripe payment links the setters send on the welcome call */
  maintenance_monthly_link?: string | null;
  maintenance_yearly_link?: string | null;
};

/** The editable cost sheet of one sale (a gula_order_costs row). */
export type CostSheet = {
  sale_gross: number;
  vat_rate: number;
  fee_pct: number;
  fee_fixed: number;
  equipment_cost: number;
  faucet_cost: number;
  installer_cost: number;
  faucet_install_cost: number;
  shipping_cost: number;
  other_costs: number;
  other_costs_note?: string | null;
  recurring_gross_month: number;
  recurring_cost_month: number;
  fee_recurring_pct: number;
  fee_recurring_fixed: number;
  notes?: string | null;
};

export type OrderCostsRecord = Omit<CostSheet, "shipping_cost"> & {
  id: Identifier;
  order_id: Identifier;
  updated_at: string;
  updated_by?: Identifier | null;
};

/** What Stripe has really collected from the client for maintenance and rental. */
export type RecurringActuals = {
  collectedGross: number;
  paymentsCount: number;
  monthsActive: number;
};

export type ProfitResult = {
  saleNet: number;
  saleVat: number;
  saleFee: number;
  directCosts: number;
  /** What the setters earn on this sale (sale and maintenance commissions) */
  commission: number;
  saleProfit: number;
  recurringNetMonth: number;
  recurringFeeMonth: number;
  recurringProfitMonth: number;
  recurringCollectedNet: number;
  recurringFeesToDate: number;
  recurringCostsToDate: number;
  recurringProfitToDate: number;
  revenueNetToDate: number;
  profitToDate: number;
  marginPct: number | null;
  paybackMonths: number | null;
};

export const RECURRING_KINDS = ["maintenance", "rental"];
const LIVE_STATUSES = ["active", "trialing", "past_due"];
const DAYS_PER_MONTH = 30.44;

const round2 = (value: number) => Math.round(value * 100) / 100;
const num = (value: unknown) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};
const withoutVat = (gross: number, vatRate: number) =>
  gross / (1 + vatRate / 100);
const feeOf = (gross: number, pct: number, fixed: number) =>
  gross > 0 ? (gross * pct) / 100 + fixed : 0;

export const computeProfit = (
  sheet: CostSheet,
  actuals: RecurringActuals,
  commission = 0,
): ProfitResult => {
  const vat = num(sheet.vat_rate);
  const saleGross = num(sheet.sale_gross);
  const saleNet = withoutVat(saleGross, vat);
  const saleFee = feeOf(saleGross, num(sheet.fee_pct), num(sheet.fee_fixed));
  const directCosts =
    num(sheet.equipment_cost) +
    num(sheet.faucet_cost) +
    num(sheet.installer_cost) +
    num(sheet.faucet_install_cost) +
    num(sheet.shipping_cost) +
    num(sheet.other_costs);
  const saleProfit = saleNet - saleFee - directCosts - commission;

  const recurringPct = num(sheet.fee_recurring_pct);
  const recurringFixed = num(sheet.fee_recurring_fixed);
  const recurringGrossMonth = num(sheet.recurring_gross_month);
  const recurringNetMonth = withoutVat(recurringGrossMonth, vat);
  const recurringFeeMonth = feeOf(
    recurringGrossMonth,
    recurringPct,
    recurringFixed,
  );
  const recurringCostMonth = num(sheet.recurring_cost_month);
  const recurringProfitMonth =
    recurringNetMonth - recurringFeeMonth - recurringCostMonth;

  const collected = Math.max(0, actuals.collectedGross);
  const recurringCollectedNet = withoutVat(collected, vat);
  const recurringFeesToDate =
    collected > 0
      ? (collected * recurringPct) / 100 +
        recurringFixed * actuals.paymentsCount
      : 0;
  const recurringCostsToDate =
    collected > 0 ? recurringCostMonth * actuals.monthsActive : 0;
  const recurringProfitToDate =
    recurringCollectedNet - recurringFeesToDate - recurringCostsToDate;

  const revenueNetToDate = saleNet + recurringCollectedNet;
  const profitToDate = saleProfit + recurringProfitToDate;

  return {
    saleNet: round2(saleNet),
    saleVat: round2(saleGross - saleNet),
    saleFee: round2(saleFee),
    directCosts: round2(directCosts),
    commission: round2(commission),
    saleProfit: round2(saleProfit),
    recurringNetMonth: round2(recurringNetMonth),
    recurringFeeMonth: round2(recurringFeeMonth),
    recurringProfitMonth: round2(recurringProfitMonth),
    recurringCollectedNet: round2(recurringCollectedNet),
    recurringFeesToDate: round2(recurringFeesToDate),
    recurringCostsToDate: round2(recurringCostsToDate),
    recurringProfitToDate: round2(recurringProfitToDate),
    revenueNetToDate: round2(revenueNetToDate),
    profitToDate: round2(profitToDate),
    marginPct:
      revenueNetToDate > 0
        ? Math.round((profitToDate / revenueNetToDate) * 1000) / 10
        : null,
    // Months of fees still needed to pay back a sale sold at a loss (rentals)
    paybackMonths:
      profitToDate < 0 && recurringProfitMonth > 0
        ? Math.ceil(-profitToDate / recurringProfitMonth)
        : null,
  };
};

/** Payment fee of the method the client used, from the usual fees. */
export const feeForMethod = (
  method: string | null | undefined,
  settings: CostSettings,
): { pct: number; fixed: number } => {
  switch (method) {
    case "klarna":
      return {
        pct: num(settings.fee_klarna_pct),
        fixed: num(settings.fee_klarna_fixed),
      };
    case "financing":
      return {
        pct: num(settings.fee_financing_pct),
        fixed: num(settings.fee_financing_fixed),
      };
    case "stripe_installments":
      // Three card charges: the fixed part is paid three times
      return {
        pct: num(settings.fee_card_pct),
        fixed: round2(num(settings.fee_card_fixed) * 3),
      };
    case "transfer":
    case "cash":
    case "rental":
      return { pct: 0, fixed: 0 };
    default:
      return {
        pct: num(settings.fee_card_pct),
        fixed: num(settings.fee_card_fixed),
      };
  }
};

/** Monthly amount (VAT included) of the live maintenance and rental plans. */
export const recurringMonthlyGross = (subscriptions: GulaSubscription[]) =>
  round2(
    subscriptions
      .filter(
        (subscription) =>
          RECURRING_KINDS.includes(subscription.kind ?? "maintenance") &&
          LIVE_STATUSES.includes(subscription.status),
      )
      .reduce(
        (sum, subscription) =>
          sum +
          (subscription.billing_interval === "year"
            ? num(subscription.amount) / 12
            : num(subscription.amount)),
        0,
      ),
  );

export const recurringActuals = (
  payments: GulaPayment[],
  now: Date,
): RecurringActuals => {
  const recurring = payments.filter(
    (payment) =>
      payment.status === "paid" && RECURRING_KINDS.includes(payment.kind),
  );
  const firstPaid = recurring
    .map((payment) => payment.paid_at)
    .filter((date): date is string => !!date)
    .sort()[0];
  const monthsActive = firstPaid
    ? Math.max(
        1,
        Math.ceil(
          (now.getTime() - new Date(firstPaid).getTime()) /
            (DAYS_PER_MONTH * 864e5),
        ),
      )
    : 0;
  return {
    collectedGross: round2(
      recurring.reduce((sum, payment) => sum + num(payment.amount), 0),
    ),
    paymentsCount: recurring.length,
    monthsActive,
  };
};

/** Months between two membrane changes. */
export const MEMBRANE_MONTHS = 48;

/**
 * Monthly cost of keeping a client on a plan: the yearly filters and their
 * shipping, plus a new membrane (and its shipping) every four years.
 */
export const planCostPerMonth = (settings: CostSettings) =>
  round2(
    (num(settings.filters_year_cost) +
      num(settings.filters_shipping_year_cost)) /
      12 +
      (num(settings.membrane_cost) + num(settings.membrane_shipping_cost)) /
        MEMBRANE_MONTHS,
  );

export type PlanCycle = {
  months: number;
  gross: number;
  net: number;
  fees: number;
  filters: number;
  membrane: number;
  profit: number;
  profitPerMonth: number;
};

/**
 * A maintenance plan measured over a full membrane cycle (four years): what
 * the client pays, minus VAT, Stripe fees, four yearly filters and one
 * membrane, each with its shipping.
 */
export const planOverCycle = (
  monthlyGross: number,
  settings: CostSettings,
): PlanCycle => {
  const months = MEMBRANE_MONTHS;
  const gross = monthlyGross * months;
  const net = withoutVat(gross, num(settings.vat_rate));
  const fees =
    months *
    feeOf(
      monthlyGross,
      num(settings.fee_recurring_pct),
      num(settings.fee_recurring_fixed),
    );
  const filters =
    (months / 12) *
    (num(settings.filters_year_cost) +
      num(settings.filters_shipping_year_cost));
  const membrane =
    num(settings.membrane_cost) + num(settings.membrane_shipping_cost);
  const profit = net - fees - filters - membrane;
  return {
    months,
    gross: round2(gross),
    net: round2(net),
    fees: round2(fees),
    filters: round2(filters),
    membrane: round2(membrane),
    profit: round2(profit),
    profitPerMonth: round2(profit / months),
  };
};

/** First draft of a sale's cost sheet, from the order and the usual costs. */
export const defaultCostSheet = ({
  order,
  settings,
  subscriptions,
}: {
  order: GulaOrder;
  settings: CostSettings;
  subscriptions: GulaSubscription[];
}): CostSheet => {
  const isRental = order.payment_method === "rental";
  const isThreeWay = order.product === "blua_three_way_tap";
  const fee = feeForMethod(order.payment_method, settings);
  const recurringGross = recurringMonthlyGross(subscriptions);
  return {
    sale_gross: isRental ? 0 : num(order.price),
    vat_rate: num(settings.vat_rate),
    fee_pct: fee.pct,
    fee_fixed: fee.fixed,
    equipment_cost: num(settings.equipment_cost),
    faucet_cost: isThreeWay ? num(settings.three_way_faucet_cost) : 0,
    installer_cost: isThreeWay
      ? num(settings.install_three_way_cost)
      : num(settings.install_aux_cost),
    faucet_install_cost: 0,
    shipping_cost:
      order.shipping_cost != null
        ? num(order.shipping_cost)
        : isThreeWay
          ? num(settings.shipping_three_way_cost)
          : num(settings.shipping_aux_cost),
    other_costs: 0,
    other_costs_note: null,
    recurring_gross_month: recurringGross,
    recurring_cost_month: recurringGross > 0 ? planCostPerMonth(settings) : 0,
    fee_recurring_pct: num(settings.fee_recurring_pct),
    fee_recurring_fixed: num(settings.fee_recurring_fixed),
    notes: null,
  };
};

/** The saved sheet when there is one (shipping always comes from the order). */
export const sheetFromRecord = (
  record: OrderCostsRecord,
  order: GulaOrder,
  fallbackShipping: number,
): CostSheet => ({
  sale_gross: num(record.sale_gross),
  vat_rate: num(record.vat_rate),
  fee_pct: num(record.fee_pct),
  fee_fixed: num(record.fee_fixed),
  equipment_cost: num(record.equipment_cost),
  faucet_cost: num(record.faucet_cost),
  installer_cost: num(record.installer_cost),
  faucet_install_cost: num(record.faucet_install_cost),
  shipping_cost:
    order.shipping_cost != null ? num(order.shipping_cost) : fallbackShipping,
  other_costs: num(record.other_costs),
  other_costs_note: record.other_costs_note ?? null,
  recurring_gross_month: num(record.recurring_gross_month),
  recurring_cost_month: num(record.recurring_cost_month),
  fee_recurring_pct: num(record.fee_recurring_pct),
  fee_recurring_fixed: num(record.fee_recurring_fixed),
  notes: record.notes ?? null,
});
